import React, { useState } from "react";
import { Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import JunkFolderHint from "./JunkFolderHint";

export default function Newsletter({ variant = "block" }) {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [code, setCode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      const { data } = await api.post("/newsletter/subscribe", { email });
      setCode(data.code || "WELCOME10");
      setSubmitted(true);
      toast.success("Welcome to Peptigen Labs — discount code unlocked");
    } catch {
      toast.error("Subscription failed — please try again");
    }
  };

  if (variant === "compact") {
    return (
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2" data-testid="newsletter-compact">
        {submitted ? (
          <div className="flex items-center gap-2 px-3 py-2 border border-[#A6FF00] rounded-sm">
            <CheckCircle2 size={14} className="text-[#A6FF00]" />
            <span className="text-xs text-[#F7FAFC]">Use <span className="text-[#A6FF00] pg-mono font-bold">{code}</span> for 10% off</span>
          </div>
        ) : (
          <>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="pg-input flex-1 text-sm"
              data-testid="newsletter-email-compact"
            />
            <button type="submit" className="pg-btn-primary text-xs whitespace-nowrap" data-testid="newsletter-submit-compact">
              Get 10% Off
            </button>
          </>
        )}
      </form>
    );
  }

  return (
    <section className="relative" data-testid="newsletter-block">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div
          className="pg-card p-10 lg:p-14 relative overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, rgba(0,194,214,0.08) 0%, rgba(166,255,0,0.04) 100%), #111A2B",
            borderColor: "rgba(166,255,0,0.3)",
          }}
        >
          <div className="absolute -top-3 left-8 px-3 py-1 bg-[#0B1220] border border-[#A6FF00] rounded-sm">
            <span className="pg-mono text-[10px] uppercase tracking-[0.22em] text-[#A6FF00]">10% Off</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center relative z-10">
            <div>
              <div className="pg-micro mb-3">// Newsletter</div>
              <h2 className="text-[#F7FAFC] text-3xl lg:text-4xl pg-display mb-3" style={{ fontFamily: "'Exo 2'" }}>
                Save 10% on your first order
              </h2>
              <p className="text-[#9CA8B8] leading-relaxed">
                Join Peptigen Labs research updates. New batch arrivals, restock alerts, and a one-time 10% welcome discount on your first UK order.
              </p>
              <p className="text-[10px] pg-mono uppercase tracking-[0.22em] text-[#9CA8B8] mt-3">
                For UK research customers only · No spam · Unsubscribe at any time
              </p>
            </div>
            <div>
              {submitted ? (
                <div className="space-y-3">
                  <div className="text-center p-8 border border-[#A6FF00]/40 bg-[#A6FF00]/5 rounded-sm">
                    <CheckCircle2 size={36} className="text-[#A6FF00] mx-auto mb-3" />
                    <div className="pg-micro mb-2">Welcome aboard</div>
                    <div className="text-[#F7FAFC] pg-heading text-xl mb-1" style={{ fontFamily: "'Rajdhani'" }}>Use this at checkout:</div>
                    <div className="text-[#A6FF00] pg-display text-3xl tracking-widest" style={{ fontFamily: "'Exo 2'" }}>{code}</div>
                    <div className="text-xs text-[#9CA8B8] mt-2">10% off — applies on your first order</div>
                  </div>
                  <JunkFolderHint email={email} what="your welcome email" size="sm" />
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-3">
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA8B8]" />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.co.uk"
                      className="pg-input pl-10 w-full"
                      data-testid="newsletter-email"
                    />
                  </div>
                  <button type="submit" className="pg-btn-checkout" data-testid="newsletter-submit">
                    Unlock 10% Discount <ArrowRight size={18} />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
